import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BellRing, Smartphone, Mail, Check } from "lucide-react";

const ROLE_LABEL = { admin: "Admin", direzione: "Direzione", segreteria: "Segreteria" };

function PersonChip({ s, on, onToggle, testId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={on}
      data-testid={testId}
      className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors duration-150 ${on ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white text-stone-700 border-stone-300 hover:border-[#1C352D]"}`}
    >
      {on && <Check size={12} />}
      <span className="font-medium">{s.name || s.email}</span>
      <span className={on ? "text-white/70" : "text-stone-400"}>{ROLE_LABEL[s.role]}</span>
      <Smartphone size={12} className={s.push_devices ? (on ? "text-emerald-300" : "text-emerald-600") : "opacity-30"} title={s.push_devices ? "Notifiche attive sul telefono" : "Notifiche non attivate"} />
    </button>
  );
}

function EventCard({ ev, staff, selected, onChange }) {
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <Card className="p-5 border border-stone-200 rounded-xl shadow-none" data-testid={`staff-notify-event-${ev.key}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="font-medium text-[#1C352D] text-sm">{ev.label}</div>
        <span className="text-[11px] text-stone-500" data-testid={`staff-notify-summary-${ev.key}`}>
          {selected.length ? `${selected.length} destinatari · push + email` : ev.key === "messaggio" ? "Nessuno scelto: push a chi segue la conversazione" : "Nessuno scelto: push a tutto lo staff"}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {staff.map((s) => (
          <PersonChip key={s.id} s={s} on={selected.includes(s.id)} onToggle={() => toggle(s.id)} testId={`staff-notify-${ev.key}-${s.id}`} />
        ))}
      </div>
    </Card>
  );
}

export default function StaffNotifications() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["staff-notify-config"],
    queryFn: async () => (await api.get("/staff-notifications/config")).data,
  });
  const [draft, setDraft] = useState({});
  useEffect(() => { if (data) setDraft(data.assignments); }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/staff-notifications/config", { events: draft })).data,
    onSuccess: () => { toast.success("Notifiche staff salvate"); qc.invalidateQueries({ queryKey: ["staff-notify-config"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Errore salvataggio"),
  });

  if (!data) return <div className="p-10 text-sm text-stone-500">Caricamento…</div>;
  const noPush = data.staff.filter((s) => !s.push_devices);
  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1100px] mx-auto" data-testid="staff-notifications-page">
      <Link to="/impostazioni" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4"><ArrowLeft size={13} /> Impostazioni</Link>
      <PageHeader
        title="Notifiche staff"
        subtitle="Per ogni evento scegli chi riceve la notifica push e l'email. Se non scegli nessuno, la notifica push arriva a tutto lo staff."
        actions={<Button onClick={() => save.mutate()} disabled={save.isPending} className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]" data-testid="staff-notify-save-btn">{save.isPending ? "Salvataggio…" : "Salva"}</Button>}
      />
      <div className="flex flex-wrap gap-4 text-[11px] text-stone-500 mb-5">
        <span className="inline-flex items-center gap-1.5"><BellRing size={12} /> Push sul telefono</span>
        <span className="inline-flex items-center gap-1.5"><Mail size={12} /> Email all'indirizzo dell'utente</span>
        <span className="inline-flex items-center gap-1.5"><Smartphone size={12} className="text-emerald-600" /> verde = notifiche attivate sul telefono</span>
      </div>
      {noPush.length > 0 && (
        <div className="mb-5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="staff-notify-no-push">
          Non hanno ancora attivato le notifiche sul telefono (riceveranno solo l'email): {noPush.map((s) => s.name || s.email).join(", ")}.
        </div>
      )}
      <div className="space-y-4">
        {data.events.map((ev) => (
          <EventCard key={ev.key} ev={ev} staff={data.staff} selected={draft[ev.key] || []} onChange={(v) => setDraft({ ...draft, [ev.key]: v })} />
        ))}
      </div>
      <div className="text-[11px] text-stone-500 mt-5">L'email per le nuove richieste di permesso continua ad arrivare anche a scuolamissionariardm@cometralci.com.</div>
    </div>
  );
}
